import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Col,
  DatePicker,
  Row,
  Spin,
  Table,
  Typography,
  message,
  Statistic,
  Divider,
} from "antd";
import { AppContext } from "../context/AppContext";
import DoubleEntryService from "../utils/doubleEntryService";
import moment from "moment";

const { Title, Text } = Typography;

const formatCurrency = (value) =>
  `ZMW ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const DoubleEntryBalanceSheet = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [balanceSheetData, setBalanceSheetData] = useState(null);
  const [asOfDate, setAsOfDate] = useState(moment());

  useEffect(() => {
    fetchBalanceSheet();
  }, [company?.id, asOfDate]);

  const fetchBalanceSheet = async () => {
    if (!company?.id || !token) return;

    setLoading(true);
    try {
      const data = await DoubleEntryService.getBalanceSheet({
        company: company.id,
        token,
        asOfDate: asOfDate.format("YYYY-MM-DD"),
      });

      setBalanceSheetData(data);
    } catch (error) {
      console.error("Error fetching balance sheet:", error);
      message.error("Failed to load balance sheet");
    } finally {
      setLoading(false);
    }
  };

  const accountColumns = [
    {
      title: "Account",
      dataIndex: "name",
      key: "name",
      render: (name, record) => {
        // Indent accumulated depreciation and net fixed assets
        if (record.code === "1510" || record.code === "1520") {
          return <span style={{ paddingLeft: 20 }}>{name}</span>;
        }
        // Bold net fixed assets
        if (record.code === "1520" || record.isCalculated) {
          return <Text strong>{name}</Text>;
        }
        return name;
      },
    },
    {
      title: "Amount",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      render: (value, record) => {
        const formatted = formatCurrency(Math.abs(value));
        // Show accumulated depreciation in parentheses
        if (record.code === "1510") {
          return <Text type="danger">({formatted})</Text>;
        }
        // Bold net fixed assets
        if (record.code === "1520" || record.isCalculated) {
          return <Text strong>{formatted}</Text>;
        }
        return formatted;
      },
    },
  ];

  const assetsData = balanceSheetData
    ? Object.values(balanceSheetData.assets)
    : [];
  const liabilitiesData = balanceSheetData
    ? Object.values(balanceSheetData.liabilities)
    : [];
  const equityData = balanceSheetData
    ? Object.values(balanceSheetData.equity)
    : [];

  const isBalanced =
    balanceSheetData &&
    Math.abs(
      balanceSheetData.totalAssets -
        (balanceSheetData.totalLiabilities + balanceSheetData.totalEquity)
    ) < 0.01;

  return (
    <div className="page-container">
      <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 24 }}>
        <Col xs={24} md={16}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Balance Sheet (Double-Entry)
          </Title>
          <Text type="secondary">
            Financial position as of {asOfDate.format("MMMM DD, YYYY")}
          </Text>
        </Col>
        <Col xs={24} md={8}>
          <DatePicker
            value={asOfDate}
            onChange={(date) => date && setAsOfDate(date)}
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
          />
        </Col>
      </Row>

      <Spin spinning={loading}>
        {balanceSheetData && (
          <>
            <Row gutter={[16, 16]}>
              {/* ASSETS */}
              <Col xs={24} lg={12}>
                <Card
                  title={
                    <Text strong style={{ fontSize: 16 }}>
                      ASSETS
                    </Text>
                  }
                >
                  <Table
                    dataSource={assetsData}
                    columns={accountColumns}
                    rowKey="code"
                    pagination={false}
                    size="small"
                    summary={() => (
                      <Table.Summary.Row
                        style={{ backgroundColor: "#e6f7ff" }}
                      >
                        <Table.Summary.Cell>
                          <Text strong>Total Assets</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          <Text strong style={{ fontSize: 16 }}>
                            {formatCurrency(balanceSheetData.totalAssets)}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </Card>
              </Col>

              {/* LIABILITIES & EQUITY */}
              <Col xs={24} lg={12}>
                <Card
                  title={
                    <Text strong style={{ fontSize: 16 }}>
                      LIABILITIES & EQUITY
                    </Text>
                  }
                >
                  {/* Liabilities Section */}
                  <Text strong>Liabilities</Text>
                  <Table
                    dataSource={liabilitiesData}
                    columns={accountColumns}
                    rowKey="code"
                    pagination={false}
                    size="small"
                    style={{ marginTop: 8 }}
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell>
                          <Text>Total Liabilities</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          <Text>
                            {formatCurrency(
                              balanceSheetData.totalLiabilities
                            )}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />

                  <Divider />

                  {/* Equity Section */}
                  <Text strong>Equity</Text>
                  <Table
                    dataSource={equityData}
                    columns={accountColumns}
                    rowKey="code"
                    pagination={false}
                    size="small"
                    style={{ marginTop: 8 }}
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell>
                          <Text>Total Equity</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          <Text>
                            {formatCurrency(balanceSheetData.totalEquity)}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />

                  <Divider />

                  {/* Total Liabilities + Equity */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "12px 8px",
                      backgroundColor: "#e6f7ff",
                      borderRadius: 4,
                    }}
                  >
                    <Text strong>Total Liabilities + Equity</Text>
                    <Text strong style={{ fontSize: 16 }}>
                      {formatCurrency(
                        balanceSheetData.totalLiabilities +
                          balanceSheetData.totalEquity
                      )}
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Accounting Equation Verification */}
            <Card style={{ marginTop: 16 }}>
              <Row gutter={16} align="middle">
                <Col xs={24} sm={8}>
                  <Statistic
                    title="Total Assets"
                    value={balanceSheetData.totalAssets}
                    precision={2}
                    prefix="ZMW"
                    valueStyle={{ color: "#3f8600" }}
                  />
                </Col>
                <Col xs={24} sm={1} style={{ textAlign: "center" }}>
                  <Title level={4}>=</Title>
                </Col>
                <Col xs={24} sm={7}>
                  <Statistic
                    title="Total Liabilities"
                    value={balanceSheetData.totalLiabilities}
                    precision={2}
                    prefix="ZMW"
                    valueStyle={{ color: "#cf1322" }}
                  />
                </Col>
                <Col xs={24} sm={1} style={{ textAlign: "center" }}>
                  <Title level={4}>+</Title>
                </Col>
                <Col xs={24} sm={7}>
                  <Statistic
                    title="Total Equity"
                    value={balanceSheetData.totalEquity}
                    precision={2}
                    prefix="ZMW"
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Col>
              </Row>

              <Divider />

              <div style={{ textAlign: "center" }}>
                {isBalanced ? (
                  <Text
                    strong
                    style={{ fontSize: 18, color: "#52c41a" }}
                  >
                    ✓ Balance Sheet is Balanced
                  </Text>
                ) : (
                  <Text
                    strong
                    style={{ fontSize: 18, color: "#ff4d4f" }}
                  >
                    ✗ Balance Sheet is Out of Balance
                  </Text>
                )}
                <br />
                <Text type="secondary">
                  Assets = Liabilities + Equity (Accounting Equation)
                </Text>
              </div>
            </Card>

            {/* Explanation */}
            <Card style={{ marginTop: 16 }}>
              <Title level={5}>Understanding the Balance Sheet</Title>
              <Text>
                The balance sheet shows your company's financial position at a
                specific point in time:
              </Text>
              <ul>
                <li>
                  <Text strong>Assets</Text> - What your company owns (Cash,
                  Loans Receivable, etc.)
                </li>
                <li>
                  <Text strong>Liabilities</Text> - What your company owes
                  (Investor Capital, Customer Deposits, etc.)
                </li>
                <li>
                  <Text strong>Equity</Text> - The owner's stake in the company
                </li>
              </ul>
              <Text>
                The fundamental accounting equation must always be true:{" "}
                <Text strong>Assets = Liabilities + Equity</Text>
              </Text>
            </Card>
          </>
        )}

        {!loading && !balanceSheetData && (
          <Card>
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Text type="secondary">
                No accounting data available for the selected date.
              </Text>
            </div>
          </Card>
        )}
      </Spin>
    </div>
  );
};

export default DoubleEntryBalanceSheet;
